import { useTranslation } from "react-i18next";
import useWindowSize from "../../../common/hooks/useWindowSize";
import { IconCarrot } from "../../../common/icons/IconCarrot/IconCarrot";
import { Requirements } from "../../../services/GroupGrowth/groupGrowth.types";
import { SSIEligibility } from "../../../services/StrongStart/ssiAPI.types";
import DonutProgressCircle from "../DonutProgressCard/DonutProgressCards";
import { EligibilityMonthsRow } from "./EligibilityMonthsRow/EligibilityMonthsRow";
import defaultContent from "../../../content/ssiUplineReport";
import "./PreWindowRow.scss";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { useState } from "react";
import { useAppDispatch } from "../../../store";
import { openProfileModal } from "../../../reducers/ssiUplineReport";
import { track } from "../../../common/util/tealium";
import TEALIUM from "../../../common/enums/tealium-enums";

export type Props = {
  aboName: string;
  aff: string;
  aboLocalName: string;
  aboNum: string;
  sponsorName: string;
  sponsorNum: string;
  eligibilityWindow: SSIEligibility;
  requirements: Requirements;
  isLoading: boolean;
  isHighlighted: boolean;
  isDesktopView: boolean;
};

export const SSIPreWindowRow = ({
  aboName,
  aboLocalName,
  aboNum,
  aff,
  sponsorName,
  sponsorNum,
  eligibilityWindow,
  requirements,
  isLoading,
  isHighlighted = false,
  isDesktopView,
}: Props) => {
  const { t } = useTranslation(["ssiUplineReport"]);
  const [isCollapsed, setIsCollasped] = useState(true);
  const dispatch = useAppDispatch();

  const renderAboNameNum = (aboName: string, aboNum: string) => {
    const renderedAboName = aboName || t("common:confidentialUser", defaultContent.confidentialUser as string);

    return (
      <div className="SSIPreWindowRow__column SSIPreWindowRow__aboNameNum">
        <span className="SSIPreWindowRow__column--title">
          <LeafRectangleLoader
            isLoading={isLoading}
            width="8rem"
            height="1rem"
            margin="0"
            View={() => <>{renderedAboName}</>}
          />
        </span>
        <span className="SSIPreWindowRow__column--subtext">
          <LeafRectangleLoader
            isLoading={isLoading}
            width="4rem"
            height="1rem"
            margin=".5rem 0 0 0"
            View={() => <>#{aboNum}</>}
          />
        </span>
      </div>
    );
  };

  const renderSponsor = (sponsorName: string) => {
    const renderedSponsorName = sponsorName || t("common:confidentialUser", defaultContent.confidentialUser as string);

    return (
      <div className="SSIPreWindowRow__column">
        <span className="SSIPreWindowRow__column--title">
          <LeafRectangleLoader
            isLoading={isLoading}
            width="6rem"
            height="1rem"
            margin="0"
            View={() => <>{t("sponsor", defaultContent.sponsor as string)}</>}
          />
        </span>
        <span className="SSIPreWindowRow__column--subtext">
          <LeafRectangleLoader
            isLoading={isLoading}
            width="4rem"
            height="1rem"
            margin=".5rem 0 0 0"
            View={() => <>{renderedSponsorName}</>}
          />
        </span>
      </div>
    );
  };

  const renderProfile = () => {
    return (
      <LeafRectangleLoader
        isLoading={isLoading}
        width="5rem"
        height="1.6rem"
        margin="0"
        View={() => (
          <button
            id={`PreWindowProfileButton${aboNum}`}
            onClick={() => {
              dispatch(
                openProfileModal({
                  aboNo: aboNum,
                  affNo: aff,
                  sponsorNo: sponsorNum,
                  sponsorAff: aff,
                  eligibilityWindow,
                  showEligibilityWindow: false,
                  show: true,
                  previousElementID: `PreWindowProfileButton${aboNum}`,
                }),
              );
              track(TEALIUM.U_TAG_METHOD.LINK, "", "pre-window", "SSI_Upline_Report", "", "ABO_profile");
            }}
          >
            <div className="SSIPreWindowRow__profileButtonContent">
              <div>
                <span>{t("profile", defaultContent.profile as string)}</span>
                <div className="SSIPreWindowRow__profileButtonContent--underline"></div>
              </div>
              <IconCarrot orientation="right" color="black" />
            </div>
          </button>
        )}
      />
    );
  };

  const renderRequirements = (requirements: Requirements, layout: "desktop" | "mobile", abo: string) => {
    return isLoading ? (
      <DonutProgressCircle label="personal" currentAmount={0} targetAmount={0} ariaTitle="" isLoading={true} />
    ) : (
      <>
        {requirements.VolumeRequirements.map((req) => {
          return (
            <DonutProgressCircle
              met={req.Met}
              label={req.Name}
              currentAmount={req.Volume.Current.Actual}
              targetAmount={req.Volume.Target.Actual}
              ariaTitle=""
              key={`DonutProgressCircle-${layout}-${abo}-${req.Name}`}
            />
          );
        })}
        {requirements.PercentRequirements ? (
          requirements.PercentRequirements.map((req) => {
            return (
              <DonutProgressCircle
                met={req.Met}
                label={req.Name}
                currentAmount={req.Percent.Current.Actual}
                targetAmount={req.Percent.Target.Actual}
                ariaTitle=""
                key={`DonutProgressCircle-vcs-${layout}-${abo}`}
              />
            );
          })
        ) : (
          <></>
        )}
      </>
    );
  };

  const renderDesktopRow = (
    aboName: string,
    aboNum: string,
    sponsorName: string,
    requirements: Requirements,
    eligibilityWindow: SSIEligibility,
  ) => {
    return (
      <section
        className={`SSIPreWindowRow SSIPreWindowRow--desktop ${isHighlighted ? "SSIPreWindowRow--highlighted" : ""}`}
      >
        <div className="SSIPreWindowRow__start">
          {renderAboNameNum(aboName, aboNum)}
          <div className="SSIPreWindowRow__row">{renderRequirements(requirements, "desktop", aboNum)}</div>

          <EligibilityMonthsRow eligibilityWindow={eligibilityWindow} isLoading={isLoading} />
        </div>
        <div className="SSIPreWindowRow__end">
          {renderSponsor(sponsorName)}
          {renderProfile()}
        </div>
      </section>
    );
  };

  const MobileCard = (
    aboName: string,
    aboNum: string,
    sponsorName: string,
    requirements: Requirements,
    eligibilityWindow: SSIEligibility,
  ) => {
    return (
      <section
        className={`SSIPreWindowRow SSIPreWindowRow--mobile ${isHighlighted ? "SSIPreWindowRow--highlighted" : ""}`}
      >
        <div className={`SSIPreWindowRow__MobileRow `}>
          {renderAboNameNum(aboName, aboNum)}
          {renderProfile()}
        </div>
        <div
          className={`SSIPreWindowRow__MobileRow--center ${isCollapsed ? "SSIPreWindowRow__MobileRow--noBorder" : ""}`}
        >
          {renderRequirements(requirements, "mobile", aboNum)}
        </div>

        <div
          className={`SSIPreWindowRow__expandableContent ${
            isCollapsed ? "SSIPreWindowRow__expandableContent--hidden" : "SSIPreWindowRow__expandableContent--visible"
          } `}
        >
          <div className="SSIPreWindowRow__MobileEligibility">
            <EligibilityMonthsRow eligibilityWindow={eligibilityWindow} isLoading={isLoading} />
          </div>
          <div className="SSIPreWindowRow__MobileSponsor">{renderSponsor(sponsorName)}</div>
        </div>

        <button className="SSIPreWindowRow__expandButton" onClick={() => setIsCollasped(!isCollapsed)}>
          <IconCarrot orientation={isCollapsed ? "down" : "up"} color={"black"} />
        </button>
      </section>
    );
  };

  return isDesktopView
    ? renderDesktopRow(aboName, aboNum, sponsorName, requirements, eligibilityWindow)
    : MobileCard(aboName, aboNum, sponsorName, requirements, eligibilityWindow);
};
